import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, Card, CardBody, CardTitle, Badge } from 'reactstrap';
import {
    importTickets,
    previewTickets,
    clearLastTicketImportLogs,
    clearTicketsImportPreview,
    loadTickettypes,
    loadEmailLayouts,
} from '../../redux';
import { Log, Setting } from '../../models';
import { Utils } from '../../services';
import ImportInvalidMessage from '../ImportInvalidMessage/ImportInvalidMessage';

import './TicketsImporter.css';

const getTicketPricingKey = ticket => {
    if (ticket.activable_pricings?.[0])
        return ticket.activable_pricings[0];

    if (ticket.opaque?.to_activate_with)
        return ticket.opaque.to_activate_with;

    const pricingKeys = Object.keys(ticket.pricings || {});
    return pricingKeys.length === 1 ? pricingKeys[0] : null;
};

const CONTACT_IMPORT_FIELDS = [
    'title', 'firstname', 'lastname', 'email', 'disable_email_notifications',
    'company', 'rfc2397_portrait', 'age', 'birthdate', 'comment', 'sex',
    'phone', 'cellphone', 'language',
];
const ADDRESS_IMPORT_FIELDS = ['street', 'city', 'zip', 'country'];

/**
 * Preview returns fully validated tickets (status, pricings, windows, …).
 * Staged import must send only fields accepted by the engine validator.
 */
const convertToStagedImportTicket = ticket => {
    if (!ticket)
        return ticket;

    const staged = {
        _id: ticket._id,
        edition: ticket.edition,
        type: { _id: ticket.type?._id },
        contact: {},
        opaque: ticket.opaque || {},
    };

    if (ticket.type?.renewable != null)
        staged.type.renewable = ticket.type.renewable;
    if (ticket.type?.renew_type)
        staged.type.renew_type = ticket.type.renew_type;
    if (ticket.type?.people != null)
        staged.type.people = ticket.type.people;
    if (ticket.type?.screening_id)
        staged.type.screening_id = ticket.type.screening_id;

    CONTACT_IMPORT_FIELDS.forEach(key => {
        if (ticket.contact?.[key] != null)
            staged.contact[key] = ticket.contact[key];
    });

    if (ticket.contact?.address) {
        const address = {};
        ADDRESS_IMPORT_FIELDS.forEach(key => {
            if (ticket.contact.address[key] != null)
                address[key] = ticket.contact.address[key];
        });
        if (Object.keys(address).length)
            staged.contact.address = address;
    }

    if (ticket.contact?.opaque && Object.keys(ticket.contact.opaque).length)
        staged.contact.opaque = ticket.contact.opaque;

    const pricingKey = getTicketPricingKey(ticket);
    if (pricingKey)
        staged.activable_pricings = [pricingKey];

    return staged;
};

const matchPreviewItemToTicket = (item, tickets) => {
    if (!item || !tickets?.length)
        return null;

    if (item._id) {
        const byId = tickets.find(t => t._id === item._id);
        if (byId)
            return byId;
    }

    return tickets.find(t =>
        t.type?._id === item.type?._id
        && t.contact?.email === item.contact?.email
        && t.contact?.firstname === item.contact?.firstname
        && t.contact?.lastname === item.contact?.lastname
    ) || null;
};

const buildOrderedPreviewTickets = preview => {
    if (!preview?.tickets?.length)
        return [];

    const allTickets = preview.tickets;
    const seen = new Set();
    const ordered = [];

    const pushUnique = (item, action) => {
        const ticket = matchPreviewItemToTicket(item, allTickets);
        if (!ticket || seen.has(ticket))
            return;
        seen.add(ticket);
        ordered.push({ ticket, action });
    };

    (preview.inserted || []).forEach(item => pushUnique(item, 'create'));
    (preview.updated || []).forEach(item => pushUnique(item, 'check'));

    const updatedIds = new Set((preview.updated || []).map(u => u._id).filter(Boolean));
    allTickets.forEach(ticket => {
        if (seen.has(ticket))
            return;
        seen.add(ticket);
        ordered.push({
            ticket,
            action: ticket._id && updatedIds.has(ticket._id) ? 'check' : 'create',
        });
    });

    return ordered;
};

const TicketsImporter = ({ visible }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [omitEvents, setOmitEvents] = useState(false);
    const [detailsIndex, setDetailsIndex] = useState(0);
    const [importContext, setImportContext] = useState(null);

    const {
        isImporting,
        isPreviewingTickets,
        hasImportingError,
        importingErrorMessage,
        hasPreviewingError,
        previewingErrorMessage,
        ticketsImportPreview,
    } = useSelector(state => state.tickets);

    const { setting }              = useSelector(state => state.settings);
    const { lastTicketImportLogs } = useSelector(state => state.logs);
    const { tickettypes }          = useSelector(state => state.tickettypes);
    const emails                   = useSelector(state => state.emailLayouts.layouts);

    const config = useMemo(
        () => setting ? (new Setting(setting)).getTicketsImportConfiguration() : null,
        [setting]
    );

    useEffect(() => {
        if (!visible)
            return;
        dispatch(loadTickettypes());
        dispatch(loadEmailLayouts());
    }, [dispatch, visible]);

    useEffect(() => {
        setDetailsIndex(0);
    }, [ticketsImportPreview]);

    const cancel = useCallback(() => {
        setOmitEvents(false);
        setDetailsIndex(0);
        setImportContext(null);
        dispatch(clearTicketsImportPreview());
        dispatch(clearLastTicketImportLogs());
    }, [dispatch]);

    const doPreview = () => {
        dispatch(clearLastTicketImportLogs());
        dispatch(clearTicketsImportPreview());
        dispatch(previewTickets(config?.source));
    };

    const doImport = () => {
        if (!ticketsImportPreview?.tickets?.length)
            return;

        setImportContext({
            toCreate: ticketsImportPreview.inserted?.length || 0,
            toCheck: ticketsImportPreview.updated?.length || 0,
            toActivate: ticketsImportPreview.activated?.length || 0,
        });
        dispatch(clearLastTicketImportLogs());
        dispatch(importTickets(
            ticketsImportPreview.tickets.map(convertToStagedImportTicket),
            {
                source: config?.source,
                staged: true,
                ...(omitEvents ? { omit_events: true } : {})
            }
        ));
        dispatch(clearTicketsImportPreview());
    };

    const getErrorMessage = useCallback((message) => {
        if (!message)
            return t('tickets.import.an_error_occured');

        const translatedError = t(message);
        if (translatedError !== message)
            return translatedError;

        if (/^[a-z0-9_.-]+$/i.test(message))
            return t('tickets.import.an_error_occured');

        return message;
    }, [t]);

    const emailsToSend = useCallback((tickettype, pricingKey) => {
        const toSend = [];

        if (!tickettype?.opaque?.emails || !tickettype.pricings?.[pricingKey])
            return toSend;

        const pricing = tickettype.pricings[pricingKey];
        const isFree = pricing.price[Object.keys(pricing.price)[0]] === 0;

        const base = (pricingKey in tickettype.opaque.emails)
            ? tickettype.opaque.emails[pricingKey]
            : tickettype.opaque.emails.default;

        if (base?.creation?.length) {
            const email = emails?.find(entry => entry._id === base.creation);
            if (email)
                toSend.push(email);
        }

        if (isFree && base?.activation?.length) {
            const email = emails?.find(entry => entry._id === base.activation);
            if (email)
                toSend.push(email);
        }

        return toSend;
    }, [emails]);

    const hasEmailsToSend = useMemo(() => {
        if (!ticketsImportPreview?.tickets?.length)
            return false;

        return ticketsImportPreview.tickets.some(ticket => {
            if (!ticket.contact?.email?.length)
                return false;
            const tickettype = tickettypes?.find(entry => entry._id === ticket.type?._id);
            if (!tickettype)
                return false;

            const pricingKey = getTicketPricingKey(ticket);
            if (!pricingKey)
                return false;

            return emailsToSend(tickettype, pricingKey).length > 0;
        });
    }, [ticketsImportPreview, tickettypes, emailsToSend]);

    const getTickettypeName = useCallback((ticket, tickettype) => {
        if (tickettype)
            return Utils.localized_or_fallback(tickettype.name, i18n.language);

        if (ticket.type?.name)
            return Utils.localized_or_fallback(ticket.type.name, i18n.language);

        return ticket.type?._id || '';
    }, [i18n.language]);

    const getPricingLabel = useCallback((ticket, tickettype, pricingKey) => {
        const pricing = ticket.pricings?.[pricingKey] || tickettype?.pricings?.[pricingKey];
        return Utils.localized_or_fallback(pricing?.name, i18n.language) || pricingKey;
    }, [i18n.language]);

    const ticketInfo = useCallback((ticket) => {
        const fullname = [
            ticket.contact?.firstname,
            ticket.contact?.lastname
        ].filter(Boolean).join(' ').trim();
        return [
            (ticket.type?.name || {})[i18n.language],
            fullname.length > 0 ? fullname : ticket.contact?.email
        ].map(s => s?.trim()).filter(Boolean).join(' - ');
    }, [i18n.language]);

    const orderedPreviewTickets = useMemo(
        () => buildOrderedPreviewTickets(ticketsImportPreview),
        [ticketsImportPreview]
    );

    const renderPreviewResume = useMemo(() => {
        if (!ticketsImportPreview)
            return null;

        const toCreate = ticketsImportPreview.inserted?.length || 0;
        const toCheck = ticketsImportPreview.updated?.length || 0;
        const toActivate = ticketsImportPreview.activated?.length || 0;
        const byTickettype = {};

        (ticketsImportPreview.tickets || []).forEach(ticket => {
            const tickettype = tickettypes?.find(entry => entry._id === ticket.type?._id);
            const pricingKey = getTicketPricingKey(ticket);
            const typeId = ticket.type?._id;
            if (!typeId || !pricingKey)
                return;

            const pricing = ticket.pricings?.[pricingKey] || tickettype?.pricings?.[pricingKey];
            if (!pricing)
                return;

            if (!(typeId in byTickettype)) {
                byTickettype[typeId] = {
                    name: getTickettypeName(ticket, tickettype),
                    total: 0,
                    pricings: {}
                };
            }

            if (!(pricingKey in byTickettype[typeId].pricings)) {
                byTickettype[typeId].pricings[pricingKey] = {
                    name: getPricingLabel(ticket, tickettype, pricingKey),
                    total: 0,
                    emails: {}
                };
            }

            byTickettype[typeId].total += 1;
            byTickettype[typeId].pricings[pricingKey].total += 1;

            if (!ticket.contact?.email?.length || !tickettype)
                return;

            const ticketEmails = emailsToSend(tickettype, pricingKey);
            ticketEmails.forEach(email => {
                if (!(email._id in byTickettype[typeId].pricings[pricingKey].emails)) {
                    byTickettype[typeId].pricings[pricingKey].emails[email._id] = {
                        _id: email._id,
                        name: Utils.localized_or_fallback(email.name, i18n.language),
                        total: 0
                    };
                }
                byTickettype[typeId].pricings[pricingKey].emails[email._id].total += 1;
            });
        });

        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    <CardTitle>{t('tickets.import.resume_view')}</CardTitle>
                    <ul className="mb-3">
                        <li>{t('tickets.import.preview_will_create', { count: toCreate })}</li>
                        <li>{t('tickets.import.preview_will_check', { count: toCheck })}</li>
                        {toActivate > 0 && (
                            <li>{t('tickets.import.preview_will_activate', { count: toActivate })}</li>
                        )}
                    </ul>
                    {Object.values(byTickettype).map((type, index) => (
                        <div key={index} className="mt-2">
                            <b>
                                <i className="fa fa-tags" />&nbsp;
                                {t('tickets.import.x_tickets_of_type', { count: type.total, name: type.name })}
                            </b>
                            <div>
                                {Object.values(type.pricings).map((pricing, pricingIndex) => (
                                    <div className="ml-5" key={pricingIndex}>
                                        <i className="fa fa-dollar-sign" />&nbsp;
                                        {t('tickets.import.x_tickets_of_price', { count: pricing.total, name: pricing.name })}
                                        <div>
                                            {Object.values(pricing.emails).map((email, emailIndex) => (
                                                <div key={emailIndex} className="ml-5">
                                                    <i className="fa fa-envelope" />&nbsp;
                                                    {t('tickets.import.send_x_emails', { count: email.total })}&nbsp;
                                                    <Button tag={Link} color="dark" size="xs" to={`/email_layouts/edit/${email._id}`}>
                                                        <i className="fa fa-link" />&nbsp;
                                                        {email.name}
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </CardBody>
            </Card>
        );
    }, [emailsToSend, getPricingLabel, getTickettypeName, i18n.language, t, tickettypes, ticketsImportPreview]);

    const renderPreviewData = useMemo(() => {
        if (!orderedPreviewTickets.length)
            return null;

        const safeIndex = Math.min(detailsIndex, orderedPreviewTickets.length - 1);
        const { ticket, action } = orderedPreviewTickets[safeIndex];
        const tickettype = tickettypes?.find(entry => entry._id === ticket.type?._id);
        const pricingKey = getTicketPricingKey(ticket);
        const portraitSrc = ticket.contact?.rfc2397_portrait || ticket.contact?.opaque?.image_url;
        const previewRows = [
            {
                key: 'action',
                label: t('tickets.import.columns.action'),
                value: t(`tickets.import.preview_action_${action}`)
            },
            {
                key: 'number',
                label: t('tickets.import.columns.number'),
                value: t('tickets.import.preview_ticket_number', {
                    number: safeIndex + 1,
                    total: orderedPreviewTickets.length
                })
            },
            {
                key: 'type',
                label: t('tickets.import.columns.type'),
                value: getTickettypeName(ticket, tickettype)
            },
            ...(pricingKey ? [{
                key: 'pricing',
                label: t('tickets.import.columns.pricing'),
                value: getPricingLabel(ticket, tickettype, pricingKey)
            }] : []),
            ...(ticket.opaque?.eventival?.type ? [{
                key: 'eventival_type',
                label: t('tickets.import.columns.eventival_type'),
                value: ticket.opaque.eventival.type
            }] : []),
            ...(ticket.opaque?.eventival?.category ? [{
                key: 'eventival_category',
                label: t('tickets.import.columns.eventival_category'),
                value: ticket.opaque.eventival.category
            }] : []),
            ...['email', 'firstname', 'lastname'].map(key => ({
                key,
                label: t(`tickets.import.columns.${key}`),
                value: ticket.contact?.[key]
            })),
            ...(ticket.contact?.opaque?.zoning ? [{
                key: 'zoning',
                label: t('tickets.import.columns.zoning'),
                value: ticket.contact.opaque.zoning.replace(/\n+/g, ', ').replace(/,\s*$/, '')
            }] : []),
            ...(portraitSrc ? [{
                key: 'photo',
                label: t('tickets.import.columns.photo'),
                isImage: true,
                value: portraitSrc
            }] : [])
        ].filter(row => row.isImage || (row.value !== null && row.value !== undefined && String(row.value).length > 0));

        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    <CardTitle className="d-flex justify-content-between align-items-center">
                        <span>{t('tickets.import.preview_view')}</span>
                        <Badge color={action === 'create' ? 'success' : 'info'}>
                            {t(`tickets.import.preview_action_${action}`)}
                        </Badge>
                    </CardTitle>
                    <div className="d-flex gap-2 mb-2 justify-end">
                        <Button color="info" size="sm" disabled={safeIndex === 0} onClick={() => setDetailsIndex(safeIndex - 1)}>
                            <i className="fa fa-arrow-left" />
                        </Button>
                        <Button
                            color="info"
                            size="sm"
                            disabled={safeIndex >= orderedPreviewTickets.length - 1}
                            onClick={() => setDetailsIndex(safeIndex + 1)}
                        >
                            <i className="fa fa-arrow-right" />
                        </Button>
                    </div>
                    <table className="table w-100">
                        <tbody>
                            {previewRows.map(row => (
                                <tr key={row.key}>
                                    <th className="text-right" width="150px">{row.label}</th>
                                    <td className="text-left">
                                        {row.isImage ? (
                                            <img
                                                src={row.value}
                                                alt=""
                                                className="TicketsImporter-previewPhoto"
                                            />
                                        ) : row.value}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="d-flex gap-2 mt-2 justify-end">
                        <Button color="info" size="sm" disabled={safeIndex === 0} onClick={() => setDetailsIndex(safeIndex - 1)}>
                            <i className="fa fa-arrow-left" />
                        </Button>
                        <Button
                            color="info"
                            size="sm"
                            disabled={safeIndex >= orderedPreviewTickets.length - 1}
                            onClick={() => setDetailsIndex(safeIndex + 1)}
                        >
                            <i className="fa fa-arrow-right" />
                        </Button>
                    </div>
                </CardBody>
            </Card>
        );
    }, [detailsIndex, getPricingLabel, getTickettypeName, orderedPreviewTickets, t, tickettypes]);

    const importResume = useCallback(log => {
        const totalChanges = (log.inserted?.length || 0) + (log.updated?.length || 0)
            + (log.activated?.length || 0) + (log.invalid?.length || 0);
        const syncedCount = (importContext?.toCheck || 0) + (importContext?.toCreate || 0);
        const isAlreadySynced = totalChanges === 0 && syncedCount > 0;

        return (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    {t('tickets.import.resume')}
                </h5>
                <ul>
                    <li>
                        <b>{t('tickets.import.nb_inserted', { count: log.inserted?.length })}</b>
                    </li>
                    <li>
                        <b>{t('tickets.import.nb_activated', { count: log.activated?.length })}</b>
                    </li>
                    <li>
                        <b>{t('tickets.import.nb_updated', { count: log.updated?.length })}</b>
                    </li>
                    <li>
                        <b>{t('tickets.import.nb_invalid', { count: log.invalid?.length })}</b>
                    </li>
                </ul>
            </div>
            {isAlreadySynced && (
                <div className="bg-info mt-2 p-2 rounded">
                    <i className="fa fa-info-circle" />&nbsp;
                    {t('tickets.import.already_synced', { count: syncedCount })}
                </div>
            )}
            {(log.activated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        {t('tickets.import.activated', { count: log.activated.length })}
                    </h5>
                    <ul>
                    {log.activated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" rel="noopener noreferrer" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    {ticketInfo(ticket)}
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        {t('tickets.import.inserted', { count: log.inserted.length })}
                    </h5>
                    <ul>
                    {log.inserted.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" rel="noopener noreferrer" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    {ticketInfo(ticket)}
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        {t('tickets.import.updated', { count: log.updated.length })}
                    </h5>
                    <ul>
                    {log.updated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" rel="noopener noreferrer" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    {ticketInfo(ticket)}
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        {t('tickets.import.invalid', { count: log.invalid.length })}
                    </h5>
                    {log.invalid.map((ticket, i) => (
                        <div key={i} className="ticket">
                            {ticket._id ? (
                                <a target="_blank" rel="noopener noreferrer" href={`${setting?.eshop?.uri}/ticket/view/?uuid=${ticket._id}`}>
                                    <b>
                                        {ticketInfo(ticket)}
                                    </b>
                                </a>
                            ) : (
                                <b>
                                    {ticketInfo(ticket)}
                                </b>
                            )}
                            - {ticket._id}
                            <ul>
                                {Object.keys(ticket.invalid).map(key => (
                                    <ImportInvalidMessage
                                        key={key}
                                        resource="tickets"
                                        fieldKey={key}
                                        code={ticket.invalid[key]}
                                        importSource={log?.source || config?.source}
                                        entry={ticket}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
        );
    }, [config?.source, importContext, setting, t, ticketInfo]);

    const ticketsImportLogs = lastTicketImportLogs?.filter(log => log.type === Log.TICKETS_IMPORT);
    const isBusy = isImporting || isPreviewingTickets;
    const previewHasInvalid = ticketsImportPreview?.invalid?.length > 0;
    const previewHasTickets = ticketsImportPreview?.tickets?.length > 0;

    if (!visible || !config)
        return null;

    return (
        <div className="TicketsImporter">
            <div className="mb-2">
                <small>
                    {t(`tickets.import.${config?.source}_import_desc`)}
                </small>
            </div>

            {isImporting ? (
                <div className="d-flex justify-content-center mt-3">
                    <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                </div>
            ) : ticketsImportLogs?.length > 0 ? (
                <div className="mt-2 w-100">
                    <h4 className="text-center text-underline">
                        {t('tickets.import.imported_at', { date: Utils.formatLocalizedDateTime(lastTicketImportLogs[0].created_at, i18n.language, 'LLL') })}
                    </h4>
                    <br />
                    {ticketsImportLogs.map(log => importResume(log))}
                    <Button onClick={cancel} color="warning" title={t('tickets.import.do_another_import')} className="mt-2">
                        <i className="fa fa-arrow-left"></i>&nbsp;{t('tickets.import.do_another_import')}
                    </Button>
                </div>
            ) : ticketsImportPreview ? (
                <div className="mt-2 w-100">
                    {previewHasInvalid && (
                        <div className="bg-danger mt-2 p-2 rounded">
                            <h5>{t('tickets.import.invalid')}</h5>
                            {ticketsImportPreview.invalid.map((ticket, i) => (
                                <div key={i} className="ticket">
                                    <b>{ticketInfo(ticket)}</b>
                                    <ul>
                                        {Object.keys(ticket.invalid || {}).map(key => (
                                            <ImportInvalidMessage
                                                key={key}
                                                resource="tickets"
                                                fieldKey={key}
                                                code={ticket.invalid[key]}
                                                importSource={config?.source}
                                                entry={ticket}
                                            />
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {previewHasTickets && !previewHasInvalid && (
                        <>
                            <h4>
                                <i className="fa fa-check" />&nbsp;
                                {t('tickets.import.file_goods', { nb: ticketsImportPreview.tickets.length })}
                            </h4>
                            {hasEmailsToSend && (
                                <div className="form-check mt-2">
                                    <input
                                        id="ticket-integration-import-omit-events"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={omitEvents}
                                        onChange={event => setOmitEvents(event.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="ticket-integration-import-omit-events">
                                        {t('tickets.import.omit_events')}
                                    </label>
                                </div>
                            )}
                            {renderPreviewResume}
                            {renderPreviewData}
                            <ButtonGroup className="mt-3 mb-3">
                                <Button onClick={cancel} color="danger" title={t('common.cancel')}>
                                    <i className="fa fa-times"></i>&nbsp;{t('common.cancel')}
                                </Button>
                                <Button onClick={doImport} disabled={isImporting} color="info" title={t('tickets.import.do_import')}>
                                    <i className={`fa ${isImporting ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>&nbsp;{t('tickets.import.do_import')}
                                </Button>
                            </ButtonGroup>
                        </>
                    )}

                    {!previewHasTickets && !previewHasInvalid && (
                        <div className="bg-info mt-2 p-2 rounded">
                            {t('tickets.import.nothing_to_import')}
                        </div>
                    )}

                    {!(previewHasTickets && !previewHasInvalid) && (
                        <Button onClick={cancel} color="secondary" className="mt-2">
                            <i className="fa fa-arrow-left"></i>&nbsp;{t('common.back')}
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    <Button
                        onClick={doPreview}
                        disabled={isBusy}
                        color="info"
                        title={t('tickets.import.do_preview')}
                        className="TktSyncButton"
                    >
                        <i className={`fa ${isBusy ? 'fa-spinner fa-spin' : 'fa-search'}`}></i>
                        <span>&nbsp;{t('tickets.import.do_preview')}</span>
                    </Button>

                    <hr />

                    <div className="d-flex mt-2">
                        {isBusy ? (
                            <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                        ) : (
                            <div className="mt-2 w-100">
                                {hasPreviewingError && (
                                    <div className="mt-2 bg-danger p-2 rounded">
                                        {getErrorMessage(previewingErrorMessage)}
                                    </div>
                                )}
                                {hasImportingError && (
                                    <div className="mt-2 bg-danger p-2 rounded">
                                        {getErrorMessage(importingErrorMessage)}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TicketsImporter;
